import classNames from "classnames"
import { useState, useEffect } from "react"

interface StepperProps {
  value: number
  onChange: (val: number) => void
  min?: number
  max: number
  step?: number
  disabled?: boolean
}

const Stepper: React.FC<StepperProps> = ({
  max,
  onChange,
  value,
  min = 0,
  step = 1,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value.toString())
  const minDisabled = value - step < min || disabled
  const maxDisabled = value + step > max || disabled

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value.toString())
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    // Allow empty input for better UX
    if (newValue === "") {
      setInputValue("")
      return
    }

    // Only allow numbers
    if (!/^\d*$/.test(newValue)) {
      return
    }

    const numValue = parseInt(newValue)

    // If valid number within range, update both input and parent
    if (!isNaN(numValue)) {
      if (numValue >= min && numValue <= max) {
        setInputValue(newValue)
        onChange(numValue)
      } else if (numValue < min) {
        setInputValue(min.toString())
        onChange(min)
      } else if (numValue > max) {
        setInputValue(max.toString())
        onChange(max)
      }
    }
  }

  const handleBlur = () => {
    // On blur, ensure the value is within bounds
    const numValue = parseInt(inputValue)
    if (isNaN(numValue) || numValue < min) {
      setInputValue(min.toString())
      onChange(min)
    } else if (numValue > max) {
      setInputValue(max.toString())
      onChange(max)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, arrows
    if ([8, 9, 13, 27, 37, 38, 39, 40].includes(e.keyCode)) {
      return
    }

    // Prevent non-numeric input
    if (!/^\d$/.test(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <div
      className={classNames(
        "grid grid-cols-[min-content_auto_min-content] w-60 items-center bg-[#121212] border-[#23201b] border text-primary px-4 py-2 text-lg drop-shadow-2xl rounded-md",
        { "opacity-50": disabled }
      )}
    >
      <div
        className={classNames(
          "text-primary text-xl sm:text-2xl cursor-pointer select-none",
          {
            "text-gray-500": minDisabled,
            "cursor-not-allowed": disabled,
          }
        )}
        onClick={() => {
          if (!minDisabled) onChange(value - step)
        }}
      >
        -
      </div>
      <input
        className="bg-transparent font-third w-full text-center outline-none border-none text-primary text-lg sm:text-2xl"
        disabled={disabled}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
      />
      <div
        className={classNames(
          "text-primary text-xl cursor-pointer select-none",
          {
            "text-gray-500": maxDisabled,
            "cursor-not-allowed": disabled,
          }
        )}
        onClick={() => {
          if (!maxDisabled) onChange(value + step)
        }}
      >
        +
      </div>
    </div>
  )
}

export default Stepper
